'use client'

import { useEffect, useRef, useCallback } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import { XIcon } from '@phosphor-icons/react'
import { PromptCaptureBox } from './prompt-capture-box'
import { useProjects } from '@/projects'

interface PromptCaptureModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * Capture modal using a lightweight overlay instead of Radix Dialog.
 * Radix Dialog's scroll-lock blocks scrolling inside nested Popover portals
 * (topics, people dropdowns). This custom approach avoids that issue.
 */
export function PromptCaptureModal({
  open,
  onOpenChange,
}: PromptCaptureModalProps) {
  const { data: projects } = useProjects()
  const contentRef = useRef<HTMLDivElement>(null)

  // Auto-focus the textarea when the modal opens
  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => {
        const textarea = contentRef.current?.querySelector('textarea')
        textarea?.focus()
      }, 50)
      return () => clearTimeout(timer)
    }
  }, [open])

  // Escape key closes modal
  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        onOpenChange(false)
      }
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [open, onOpenChange])

  // Click on backdrop closes modal
  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        onOpenChange(false)
      }
    },
    [onOpenChange],
  )

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 z-50 bg-black/50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.12 }}
            onClick={handleBackdropClick}
            aria-hidden="true"
          />

          {/* Content */}
          <motion.div
            ref={contentRef}
            role="dialog"
            aria-modal="true"
            aria-label="Capture"
            data-testid="prompt-capture-modal"
            className="fixed top-[50%] left-[50%] z-50 w-full max-w-[calc(100%-2rem)] outline-none sm:max-w-[580px]"
            initial={{ opacity: 0, x: '-50%', y: 'calc(-50% - 8px)' }}
            animate={{ opacity: 1, x: '-50%', y: '-50%' }}
            exit={{
              opacity: 0,
              x: '-50%',
              y: 'calc(-50% - 4px)',
              transition: { duration: 0.1 },
            }}
            transition={{
              duration: 0.12,
              ease: [0.34, 1.56, 0.64, 1],
            }}
          >
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="ring-offset-background focus:ring-ring text-muted-foreground/40 hover:text-muted-foreground absolute top-3 right-3 z-10 rounded-sm p-1 transition-colors hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden"
              aria-label="Close"
            >
              <XIcon className="h-3.5 w-3.5" />
            </button>

            <PromptCaptureBox
              onSuccess={() => onOpenChange(false)}
              availableProjects={projects}
            />
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
